import THREE from "three";
import { MeshFlags } from "./MeshFlags";
import { INVALID_MAT_ID } from "../globals";

const _nullVec = new THREE.Vector4(0, 0, 0, 0);

/**
 * @typedef {Object.<number, number[] | number>} DbIdToMeshIdMapping
 * @private
 */

/**
 * @typedef {Object} FragmentsObject
 * @property {number} length
 * @property {Float32Array} boxes
 * @property {Float32Array} transforms
 * @property {string[] | number[]} materials
 * @property {Int32Array} [entityIndexes]
 * @property {Int32Array} fragId2dbId
 * @property {DbIdToMeshIdMapping} [dbId2fragId]
 * @property {Int32Array} [packIds]
 * @property {Uint8Array | Uint16Array | Uint32Array | null } visibilityFlags
 * @property {boolean} dontComputeBoxes
 * @property {Uint8Array | Uint16Array | Uint32Array | Int32Array} [geomDataIndexes]
 * @property {Int32Array} [topoIndexes]
 * @property {number} numLoaded
 *
 * @private
 */

/**
 * @callback InstancesCallback
 * @param {number | number[] | undefined} instanceIds - The ids of the affected instances or undefined if all instances are affected.
 * @returns {void}
 */

/**
 * @callback GeometryChangedCallback
 * @param {number} instanceId - The id of the instance whose geometry changed.
 * @param {BufferGeometry} geometry - The new geometry.
 * @returns {void}
 */

/**
 * @callback MaterialChangedCallback
 * @param {number} instanceId - The id of the affected instance.
 * @param {Material} material - The new material of the instance.
 * @returns {void}
 */

/**
 * @callback ThemingColorChangedCallback
 * @param {number | number[] | undefined} instanceIds - The ids of the affected instances or undefined if all instances are affected.
 * @param {Autodesk.Viewing.Math.Vector4 | undefined} color - The new theming color of the instance. Undefined means that theming is cleared.
 * @returns {void}
 */

/**
 * Visibility states for scene graph nodes and geometry instances.
 *
 * @readonly
 * @enum {number}
 * @property {number} Visible Object is fully visible.
 * @property {number} Ghosted Object is ghosted (translucent).
 * @property {number} Hidden Object is not visible.
 *
 * @alias Autodesk.Viewing.Scene.VisibilityState
 * @memberof Autodesk.Viewing.Scene
 */
export const VisibilityState = Object.freeze({
  Visible: 0,
  Ghosted: 1,
  Hidden: 2
});

const VISIBILITY_STATE_MASK = MeshFlags.MESH_VISIBLE | MeshFlags.MESH_HIDE;

/**
 * Represents the full list of all geometry instances associated with
 * a particular model. The order in the list is 1:1 with fragment list
 * in the source LMV/SVF package file.
 *
 * Note: "instance" here refers to a slot in the collection
 * (a geometry+material+transform record). The legacy `setMesh()` /
 * `getVizmesh()` API is retained because it is shared with the
 * non-SceneAPI `FragmentList` path and wraps THREE.Mesh objects.
 * @param {FragmentsObject} fragments - Fragment data parsed from an SVF file.
 * @param {number} modelId - The ID of the model.
 */
export class InstanceCollectionBase {
  modelId;
  fragments;
  isFixedSize;
  vizflags;

  // see this.areAllVisible(..). Indicates if MESH_VISIBLE flag is set for all instances (i.e., not considering culling)
  allVisible = true;

  // if true, this.allVisible is outdated and must be recomputed in this.areAllVisible.
  allVisibleDirty = true;

  // if true, this.vizflags has changed.
  vizflagsDirty = true;

  // Optional: Additional transform applied to the whole model
  matrix = null;

  // Optional bounds that limit the fragment's visibility (used for viewport bounds in 2D models)
  viewBounds = null;

  // theming (coloring based on id)
  db2ThemingColor = []; // empty if no theming is applied. A theming color db2ThemingColor[dbId] is stored as THREE.Vector4 with values in [0,1].
  themingOrGhostingIsDirty = true; // if true, the theming or ghosting state has been changed since the last call to themingOrGhostingChanged.

  // Lightweight modification event system used by WebGPU renderer for uniform updates.
  _meshSetCallbacks = [];
  _modelMatrixChangedCallbacks = [];

  /** @type {MaterialChangedCallback[]} */
  _materialChangedCallbacks = [];

  /** @type {ThemingColorChangedCallback[]} */
  _themingColorChangedCallbacks = [];

  /** @type {InstancesCallback[]} */
  _visibilityDirtyCallbacks = [];

  /** @type {InstancesCallback[]} */
  _highlightingChangedCallbacks = [];

  constructor(fragments, modelId) {
    this.modelId = modelId;
    this.fragments = fragments;
  }

  // Returns undefined if fragId has no material
  getSvfMaterialId(fragId) {
    var mat = this.getMaterial(fragId);
    return mat ? mat.svfMatId : undefined;
  }

  isFlagSet(fragId, flag) {
    return !!(this.vizflags[fragId] & flag);
  }

  /**
   * Checks whether an instance is currently loaded.
   *
   * An instance is only loaded if it has not been marked as disabled (via the MESH_NOTLOADED flag)
   * and it has not been swapped out of the working set by the OutOfCoreTileManager (via the MESH_SWAPPED_OUT_OF_CORE flag).
   *
   * @param {number} instanceId - The id of the instance
   * @returns {boolean}
   */
  isInstanceLoaded(instanceId) {
    return !this.isFlagSet(instanceId, MeshFlags.MESH_NOTLOADED) &&
    !this.isFlagSet(instanceId, MeshFlags.MESH_SWAPPED_OUT_OF_CORE);
  }

  /**
   * Set/unset flag of a fragment.
   * Note: Changing MESH_VISIBLE requires to update allVisibleDirty as well => Use setVisibility() for this case.
   * @param {number} fragId - Fragment ID.
   * @param {number} flag - Must be one of the flags defined at the beginning of this file, e.g., MESH_HIGHLIGHTED.
   * @returns {bool} False if nothing changed.
   */
  setFlagFragment(fragId, flag, value) {
    // If flag is already defined and has this value, just return false.
    var old = this.vizflags[fragId];
    if (!!(old & flag) == value) {// "!!" casts to boolean, "==" is intentional.
      return false;
    }

    // set or unset flag
    if (value) {
      this.vizflags[fragId] = old | flag;
    } else {
      this.vizflags[fragId] = old & ~flag;
    }
    this.vizflagsDirty = true;

    return true;
  }

  /**
   * Set/unset flag for all fragments, e.g. setFlagGlobal(MESH_VISIBLE, true);
   * Note: Changing MESH_VISIBLE requires to update allVisibleDirty as well => use setAllVisibility() for this case.
   * @param {number} flag - Must be one of the flags defined at the beginning of this file, e.g., MESH_HIGHLIGHTED.
   * @param {bool} value - Value to be set to the flag
   */
  setFlagGlobal(flag, value) {
    var vizflags = this.vizflags;
    var i = 0,iEnd = vizflags.length;
    if (value) {
      for (; i < iEnd; i++) {
        vizflags[i] = vizflags[i] | flag;
      }
    } else {
      var notflag = ~flag;
      for (; i < iEnd; i++) {
        vizflags[i] = vizflags[i] & notflag;
      }
    }
    this.vizflagsDirty = true;
  }

  /**
   * @param {number} id
   * @param {number} flagMask - Mask of the flags that will be affected
   * @param {number} value - State of all masked flags to be set
   * @returns {boolean} Whether the value changed
   */
  setFlagsValue(id, flagMask, value) {
    // If this actually changes value the caller probably made a mistake.
    value &= flagMask;

    // If flag is already defined and has this value, just return false.
    var oldFlags = this.vizflags[id];
    if ((oldFlags & flagMask) === value) {
      return false;
    }

    // Un-set and re-set all all flags.
    this.vizflags[id] = oldFlags & ~flagMask | value;

    return true;
  }

  /**
   * Set the visibility state (Visible, Hidden, Ghosted) of an instance.
   *
   * @param {number} instanceId
   * @param {VisibilityState} state
   * @returns {boolean} Whether this value changed
   *
   * @alias Autodesk.Viewing.Scene.InstanceCollection3D#setVisibilityState
   */
  setVisibilityState(instanceId, state) {
    let newFlags = state === VisibilityState.Hidden ? MeshFlags.MESH_HIDE : 0;
    newFlags |= state === VisibilityState.Visible ? MeshFlags.MESH_VISIBLE : 0;

    const change = this.setFlagsValue(instanceId, VISIBILITY_STATE_MASK, newFlags);
    if (!change) {
      return false;
    }

    this.vizflagsDirty = true;
    this.allVisibleDirty = true;

    // Inform other code (e.g. the renderer) about the visibility change.
    for (const callback of this._visibilityDirtyCallbacks) {
      callback(instanceId);
    }

    return true;
  }

  /**
   * Returns the visibility state (Visible, Hidden, Ghosted) of an instance.
   * @param {number} instanceId
   * @returns {VisibilityState} The visibility state of the instance.
   *
   * @alias Autodesk.Viewing.Scene.InstanceCollection3D#getVisibilityState
   */
  getVisibilityState(instanceId) {
    const flags = this.vizflags[instanceId];
    if (flags & MeshFlags.MESH_HIDE) {
      return VisibilityState.Hidden;
    }
    return flags & MeshFlags.MESH_VISIBLE ? VisibilityState.Visible : VisibilityState.Ghosted;
  }

  /**
   * Marks all fragments with the given flag as visible or hidden.
   * Works like this.setFlagGlobal(MESH_HIDE, hide), but only affects fragments with given flag.
   * @param {number} typeFlag - visibility flag of fragments to change
   * @param {bool} hide - Desired visibility status.
   */
  hideFragments(typeFlag, hide) {
    var flag = MeshFlags.MESH_HIDE;

    var vizflags = this.vizflags;
    var i = 0,iEnd = vizflags.length;
    if (hide) {
      for (; i < iEnd; i++) {
        if (vizflags[i] & typeFlag)
        vizflags[i] = vizflags[i] | flag;
      }
    } else {
      var notflag = ~flag;
      for (; i < iEnd; i++) {
        if (vizflags[i] & typeFlag)
        vizflags[i] = vizflags[i] & notflag;
      }
    }

    // Mark allVisible as outdated
    this.allVisibleDirty = true;
    this.vizflagsDirty = true;

    for (const callback of this._visibilityDirtyCallbacks) {
      callback();
    }
  }

  /**
   * Checks visibility of a fragment.
   * @param {number} frag - Fragment ID.
   * @returns {bool} True if the fragment is visible and not hidden.
   */
  isFragVisible(frag) {
    return (this.vizflags[frag] & (MeshFlags.MESH_VISIBLE | MeshFlags.MESH_HIDE)) == MeshFlags.MESH_VISIBLE;
  }

  isFragOff(frag) {
    return !!(this.vizflags[frag] & MeshFlags.MESH_HIDE);
  }

  /*
  * Returns true if a fragment was excluded from loading or unloaded.
  *
  * Note that isNotLoaded()==false does not guarantee that geometry and material are already in memory.
  * Only vice versa: If true, it will not be loaded at all.
  */
  isNotLoaded(frag) {
    return !!(this.vizflags[frag] & MeshFlags.MESH_NOTLOADED);
  }

  areAllVisible() {
    // update allVisible if any flags have changed
    if (this.allVisibleDirty) {

      // allVisible <=> MESH_VISIBLE is set for all fragments
      var vizflags = this.vizflags;
      var allVisible = true;
      for (var i = 0, iEnd = vizflags.length; i < iEnd; i++) {
        if ((vizflags[i] & MeshFlags.MESH_VISIBLE) === 0 || (vizflags[i] & MeshFlags.MESH_HIDE) === 4) {
          allVisible = false;
          break;
        }
      }

      this.allVisible = allVisible;
      this.allVisibleDirty = false;
    }

    return this.allVisible;
  }

  /**
   * Returns whether the visibility flags have been changed since the last call.
   * @returns {boolean} True if the visibility flags have been changed.
   */
  vizFlagsChanged() {
    const result = this.vizflagsDirty;
    this.vizflagsDirty = false;
    return result;
  }

  /**
   * Returns whether the theming or ghosting state has been changed since the last call.
   * @returns {boolean} True if the state has been changed.
   */
  themingOrGhostingChanged() {
    const result = this.themingOrGhostingIsDirty;
    this.themingOrGhostingIsDirty = false;
    return result;
  }

  getMaterialId(fragId) {
    var m = this.getMaterial(fragId);
    return m ? m.id : INVALID_MAT_ID;
  }

  isFragmentActive(fragId) {
    return this.hasGeometry(fragId) && this.hasMaterial(fragId);
  }

  getDbIds(fragId) {
    return this.fragments.fragId2dbId[fragId];
  }

  /**
   * Returns the theming color of an instance.
   * @param {number} instanceId
   * @returns {Vector4 | undefined} The theming color of the instance, or undefined if no theming is applied.
   */
  getThemingColor(instanceId) {
    return this.db2ThemingColor[this.fragments.fragId2dbId[instanceId]];
  }

  dtor() {

    this.fragments = null;
    this.vizflags = null;

    this.db2ThemingColor = null;

    this.matrix = null;
    this.viewBounds = null;
  }

  // This function should probably not be called outside VisibilityManager
  // in order to maintain node visibility state.
  setVisibility(fragId, value) {
    const flagChanged = this.setFlagFragment(fragId, MeshFlags.MESH_VISIBLE, value);

    if (flagChanged) {
      this.allVisibleDirty = true;

      // Inform other code (e.g. the renderer) about the visibility change.
      for (const callback of this._visibilityDirtyCallbacks) {
        callback(fragId);
      }
    }

    return flagChanged;
  }

  // Note that this function switches whole instances on/off. It cannot be used to toggle visibility of
  // single 2D objects within a single instance. For this one, use setObject2DVisible instead.
  setFragOff(fragId, value) {
    const flagChanged = this.setFlagFragment(fragId, MeshFlags.MESH_HIDE, value);

    if (flagChanged) {
      this.allVisibleDirty = true; // [HB:] Either this should be removed or this.areAllVisible should consider MESH_HIDE

      // Inform other code (e.g. the renderer) about the visibility change.
      for (const callback of this._visibilityDirtyCallbacks) {
        callback(fragId);
      }
    }

    return flagChanged;
  }

  /**
   * Set the highlighted state of an instance.
   * @param {number} instanceId
   * @param {boolean} value - Whether the instance should be highlighted or not.
   * @returns {boolean} Whether the flag has changed
   *
   * @alias Autodesk.Viewing.Scene.InstanceCollection3D#setHighlighted
   */
  setHighlighted(instanceId, value) {
    const flagChanged = this.setFlagFragment(instanceId, MeshFlags.MESH_HIGHLIGHTED, value);

    if (flagChanged) {
      // Inform other code (e.g. the renderer) about the visibility change.
      for (const callback of this._highlightingChangedCallbacks) {
        callback(instanceId);
      }
    }

    return flagChanged;
  }

  setAllVisibility(value) {
    // Inform other code (e.g. the renderer) about the visibility change.
    if (this.allVisible !== value || !value) {
      for (const callback of this._visibilityDirtyCallbacks) {
        callback();
      }
    }

    this.allVisible = value;
    this.allVisibleDirty = false;
  }

  getInverseModelMatrix() {
    if (this.matrix) {
      if (!this.invMatrix) {
        this.invMatrix = this.matrix.clone().invert();
      }

      return this.invMatrix;
    }

    return null;
  }

  /**
   * Applies a theming color that is blended with the final instance color.
   * @param {number}        dbId
   * @param {Vector4} [color] - theming color (in xyz) and intensity (in w). All components in [0,1].
   *                                  Set to undefined for 'no theming'
   *
   * @alias Autodesk.Viewing.Scene.InstanceCollection3D#setThemingColor
   */
  setThemingColor(dbId, color) {
    // Stop if color keeps the same
    var oldColor = this.db2ThemingColor[dbId];
    var colorsEqual = oldColor === color || oldColor && color && oldColor.equals(color);
    if (!colorsEqual) {
      this.db2ThemingColor[dbId] = color || undefined;
      this.themingOrGhostingIsDirty = true;

      // Requires dbId2fragId to exist for 3D models as well. Note that WebGPU models must be loaded with
      // buidDbId2FragIdMap option (see OtgLoader), so that dbId2fragId always exists.
      if (this._themingColorChangedCallbacks.length) {
        if (!color) {
          color = _nullVec;
        }
        const fragIds = this.fragments.dbId2fragId[dbId];
        if (Array.isArray(fragIds)) {
          for (const fragId of fragIds) {
            for (const callback of this._themingColorChangedCallbacks) {
              callback(fragId, color);
            }
          }
        } else if (typeof fragIds === 'number') {
          for (const callback of this._themingColorChangedCallbacks) {
            callback(fragIds, color);
          }
        }
      }
    }
  }

  /** Restore original colors for all themed shapes. */
  clearThemingColors() {
    // clear theming-color map
    this.db2ThemingColor.length = 0;
    this.themingOrGhostingIsDirty = true;
    for (const callback of this._themingColorChangedCallbacks) {
      callback();
    }
  }

  getViewBounds() {
    return this.viewBounds;
  }

  setViewBounds(bounds) {
    if (!bounds) {
      this.viewBounds = null;
      return;
    }

    this.viewBounds = this.viewBounds || new THREE.Box3();

    this.viewBounds.copy(bounds);

    // If the bounds passed are 2D set Z bounds to infinity
    if (!Object.prototype.hasOwnProperty.call(bounds.max, 'z')) {
      this.viewBounds.max.z = Infinity;
      this.viewBounds.min.z = -Infinity;
    }
  }

  // Event listening methods
  /**
   * Registers a callback that is invoked when a mesh is set for a fragment.
   * @param {function} callback The callback to invoke on the event. Called with the fragment id.
   */
  registerMeshSetCallback(callback) {
    this._meshSetCallbacks.push(callback);
  }

  /**
   * Deregisters a callback that has previously been registered via `registerMeshSetCallback`.
   * @param {function} callback The callback to deregister.
   */
  removeMeshSetCallback(callback) {
    const index = this._meshSetCallbacks.indexOf(callback);

    if (index !== -1) {
      this._meshSetCallbacks.splice(index, 1);
    }
  }

  /**
   * Registers a callback that is invoked when the model matrix changes.
   * @param {function} callback The callback to invoke on the event.
   */
  registerModelMatrixChangedCallback(callback) {
    this._modelMatrixChangedCallbacks.push(callback);
  }

  /**
   * Deregisters a callback that has previously been registered via `registerModelMatrixChangedCallback`.
   * @param {function} callback The callback to deregister.
   */
  removeModelMatrixChangedCallback(callback) {
    const index = this._modelMatrixChangedCallbacks.indexOf(callback);

    if (index !== -1) {
      this._modelMatrixChangedCallbacks.splice(index, 1);
    }
  }

  /**
   * Registers a callback that is invoked when the material of an instance is changed.
   * @param {MaterialChangedCallback} callback The callback to invoke on the event.
   *
   * @alias Autodesk.Viewing.Scene.InstanceCollection3D#registerMaterialChangedCallback
   */
  registerMaterialChangedCallback(callback) {
    this._materialChangedCallbacks.push(callback);
  }

  /**
   * Deregisters a callback that has previously been registered via `registerMaterialChangedCallback`.
   * @param {MaterialChangedCallback} callback The callback to deregister.
   *
   * @alias Autodesk.Viewing.Scene.InstanceCollection3D#removeMaterialChangedCallback
   */
  removeMaterialChangedCallback(callback) {
    const index = this._materialChangedCallbacks.indexOf(callback);

    if (index !== -1) {
      this._materialChangedCallbacks.splice(index, 1);
    }
  }

  /**
   * Registers a callback that is invoked when the theming color of an instance is changed.
   * @param {ThemingColorChangedCallback} callback The callback to invoke on the event.
   *
   * @alias Autodesk.Viewing.Scene.InstanceCollection3D#registerThemingColorChangedCallback
   */
  registerThemingColorChangedCallback(callback) {
    this._themingColorChangedCallbacks.push(callback);
  }

  /**
   * Deregisters a callback that has previously been registered via `registerThemingColorChangedCallback`.
   * @param {ThemingColorChangedCallback} callback The callback to deregister.
   *
   * @alias Autodesk.Viewing.Scene.InstanceCollection3D#removeThemingColorChangedCallback
   */
  removeThemingColorChangedCallback(callback) {
    const index = this._themingColorChangedCallbacks.indexOf(callback);

    if (index !== -1) {
      this._themingColorChangedCallbacks.splice(index, 1);
    }
  }

  /**
   * Registers a callback that is invoked when the highlighting of an instance is changed.
   * @param {InstancesCallback} callback The callback to invoke on the event.
   * @alias Autodesk.Viewing.Scene.InstanceCollection3D#registerHighlightingChangedCallback
   */
  registerHighlightingChangedCallback(callback) {
    this._highlightingChangedCallbacks.push(callback);
  }

  /**
   * Deregisters a callback that has previously been registered via `registerHighlightingChangedCallback`.
   * @param {InstancesCallback} callback The callback to deregister.
   * @alias Autodesk.Viewing.Scene.InstanceCollection3D#removeHighlightingChangedCallback
   */
  removeHighlightingChangedCallback(callback) {
    const index = this._highlightingChangedCallbacks.indexOf(callback);

    if (index !== -1) {
      this._highlightingChangedCallbacks.splice(index, 1);
    }
  }

  /**
   * Registers a callback that is invoked when the visibility of any instance changes.
   * @param {InstancesCallback} callback The callback to invoke on the event.
   *
   * @alias Autodesk.Viewing.Scene.InstanceCollection3D#registerVisibilityDirtyCallback
   */
  registerVisibilityDirtyCallback(callback) {
    this._visibilityDirtyCallbacks.push(callback);
  }

  /**
   * Deregisters a callback that has previously been registered via `registerVisibilityDirtyCallback`.
   * @param {InstancesCallback} callback The callback to deregister.
   *
   * @alias Autodesk.Viewing.Scene.InstanceCollection3D#removeVisibilityDirtyCallback
   */
  removeVisibilityDirtyCallback(callback) {
    const index = this._visibilityDirtyCallbacks.indexOf(callback);

    if (index !== -1) {
      this._visibilityDirtyCallbacks.splice(index, 1);
    }
  }

  resize(nlen) {
    // re-allocate vizflags
    const nflags = new Uint16Array(nlen);
    nflags.set(this.vizflags);
    this.vizflags = nflags;
  }
}